import HelpOutlineIcon from '@material-ui/icons/ContactSupportOutlined'
import React, { useMemo } from 'react'
import { Field, useFormikContext } from 'formik'
import { yup } from 'civic-champs-shared/api/utils'
import { StyledOutlinedTextField } from 'civic-champs-shared/formik/components'
import OrganizationAddressField from './OrganizationAddressField'
import { makeStyles } from '@material-ui/styles'
import { NumberSchema } from 'yup'
import RadioGroupFormField from 'components/RadioGroupFormField'
import { Tooltip } from 'civic-champs-shared'
import { ModalButtonsProps, OrganizationLayoutModal } from './OrganizationLayoutModal'
import { OrganizationFormData } from 'organization/types'
import OrganizationAdminLocalesFields from 'organization/component/OrganizationAdminLocalesFields'
import FeatureEnabled from 'core/feature/components/FeatureEnabled'
import { INTERNATIONALIZATION_FEATURE, useInternationalizationEnabled } from 'i18n/feature'
import { SUPPORTED_LOCALES } from 'civic-champs-translations/src'

const adminLocaleValidation = {
  supportedAdminLocales: yup
    .array()
    .of(yup.string().oneOf([...SUPPORTED_LOCALES]))
    .min(1, 'Select at least one admin language'),
  defaultAdminLocale: yup
    .string()
    .oneOf([...SUPPORTED_LOCALES])
    .test('default-in-supported', 'Default language must be one of the supported languages', function (value) {
      const supported = this.parent.supportedAdminLocales as string[] | undefined
      return !!value && !!supported?.includes(value)
    }),
}

const validationSchema = yup.object({
  address: yup
    .object({
      line1: yup
        .string()
        .min(1, 'An address is required. Please enter an address.')
        .required('An address is required. Please enter an address.')
        .typeError('An address is required. Please enter an address.'),
    })
    .required('An address is required. Please enter an address.')
    .typeError('An address is required. Please enter an address.'),
  futureEventVisibilityDays: yup.mixed().when('limitFutureEventVisibility', {
    is: true,
    then: ((yup.number() as any).id() as NumberSchema).min(1).required('Please enter a number of days'),
    else: yup.mixed().notRequired().nullable(),
  }),
  replyToEmail: yup
    .string()
    .email('Please provide a valid email address from an accepted domain.')
    .required('Please provide a global reply-to email address for outbound messages.'),
})

const useStyles = makeStyles({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  subHeader: {
    fontFamily: 'Nunito',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0.5px',
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    margin: 0,
  },
  futureEventVisibilityDays: {
    width: '208px',
    marginLeft: '46px',
  },
})

const RenderIf = ({ children }: { children: React.ReactNode }) => {
  const { values } = useFormikContext<Record<string, any>>()
  if (values.limitFutureEventVisibility) {
    return <>{children}</>
  }
  return null
}

export default function OrganizationSettingsScreen(props: {
  formData: OrganizationFormData
  buttonsProps: ModalButtonsProps
}) {
  const { formData, buttonsProps } = props

  const classes = useStyles()
  const internationalizationEnabled = useInternationalizationEnabled()
  const formValidationSchema = useMemo(
    () => (internationalizationEnabled ? validationSchema.shape(adminLocaleValidation) : validationSchema),
    [internationalizationEnabled],
  )

  return (
    <OrganizationLayoutModal buttonsProps={buttonsProps} formData={formData} validationSchema={formValidationSchema}>
      <OrganizationAddressField labelShrink label="Primary Address*" placeholder="Primary Address" vertical />
      <Field
        component={StyledOutlinedTextField}
        id="replyToEmail"
        name="replyToEmail"
        label="Reply-To Email*"
        variant="outlined"
        fullWidth
      />

      <FeatureEnabled code={INTERNATIONALIZATION_FEATURE}>
        <OrganizationAdminLocalesFields />
      </FeatureEnabled>

      <div className={classes.section}>
        <div className={classes.subHeader}>Event Visibility</div>
        <RadioGroupFormField
          name="limitFutureEventVisibility"
          valueIsBoolean
          options={[
            {
              label: 'Show all Upcoming Events on the organization’s event calendar',
              value: false,
            },
            {
              label: 'Limit how many days into the future volunteers can view events',
              value: true,
            },
          ]}
        />
        <RenderIf>
          <div className={classes.futureEventVisibilityDays}>
            <Field
              component={StyledOutlinedTextField}
              id="futureEventVisibilityDays"
              name="futureEventVisibilityDays"
              variant="outlined"
              label="Days"
              marginless
              fullWidth
            />
          </div>
        </RenderIf>
      </div>
      <div className={classes.section}>
        <div className={classes.subHeader}>
          Silent Mode
          <Tooltip
            value={
              'Enabling Silent Mode will prevent administrative actions in Civic Champs from sending any emails or texts. ' +
              'This includes inviting volunteers to groups, registration confirmations, event cancellations, etc.'
            }
          >
            <HelpOutlineIcon style={{ fontSize: 18, verticalAlign: 'middle', marginLeft: 5 }} />
          </Tooltip>
        </div>
        <RadioGroupFormField
          name="sandboxModeEnabled"
          valueIsBoolean
          options={[
            {
              label: 'Disabled: All outbound messages will be sent as expected.',
              value: false,
            },
            {
              label: 'Enabled: Prevent all outbound messages from being sent.',
              value: true,
            },
          ]}
        />
      </div>
    </OrganizationLayoutModal>
  )
}
